import { ButtonHeading } from "components";
import BootstrapTable from "components/BootstrapTable";
import MastersFilterModal from "modals/MastersFilterModal";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { IoChevronBack, IoChevronForward } from "react-icons/io5";
import { getDeletedUserList } from "_services/userService";
import useDocumentTitle from "hooks/useDocumentTitle";
import { FaCheckCircle } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const DeletedUser = () => {
  useDocumentTitle("User | MySifarish");
  const getUserData = JSON.parse(localStorage.getItem("userData"));
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [userListData, setUserListData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 20;
  const [loading, setLoading] = useState(false);
  const [exportLoad, setExportLoad] = useState(false);

  // const [filter, setFilter] = useState({
  //   userId: "",
  //   page: 1,
  //   limit: 20,
  //   keyword: "",
  // });

  const debounceRefList = useRef(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const initialFilter = {
    userId: "",
    page: 1,
    limit: 20,
    keyword: "",
  };

  const [filter, setFilter] = useState({
    ...initialFilter,
    userId: searchParams.get("userId") || "",
    page: Number(searchParams.get("page")) || 1,
    limit: Number(searchParams.get("limit")) || 20,
    keyword: searchParams.get("keyword") || "",
  });

  useEffect(() => {
    const params = {};

    Object.entries(filter).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        params[key] = value;
      }
    });

    setSearchParams(params);
  }, [filter, setSearchParams]);

  useEffect(() => {
    if (!getUserData?._id) return;

    if (debounceRefList.current) {
      clearTimeout(debounceRefList.current);
    }

    debounceRefList.current = setTimeout(() => {
      fetchDeletedUserList();
    }, 500);

    return () => {
      if (debounceRefList.current) {
        clearTimeout(debounceRefList.current);
      }
    };
  }, [filter]);

  const handlePageChange = (page) => {
    setFilter((s) => ({ ...s, page }));
  };

  const renderPagination = () => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const currentPage = filter.page;
    let pages = [];

    const createPageItem = (i) => (
      <li key={i} className={`page-item ${i === currentPage ? "active" : ""}`}>
        <a className="page-link _avPointer" onClick={() => handlePageChange(i)}>
          {i}
        </a>
      </li>
    );

    const rangeStart = Math.max(2, currentPage - 2);
    const rangeEnd = Math.min(totalPages - 1, currentPage + 2);

    pages.push(createPageItem(1));

    if (rangeStart > 2) {
      pages.push(
        <li key="dots-before" className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }

    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(createPageItem(i));
    }

    if (rangeEnd < totalPages - 1) {
      pages.push(
        <li key="dots-after" className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }

    if (totalPages > 1) {
      pages.push(createPageItem(totalPages));
    }

    return (
      <ul className="pagination">
        <li className={`page-item ${filter.page === 1 ? "disabled" : ""}`}>
          <a
            className="page-link _avPointer"
            onClick={() => handlePageChange(filter.page - 1)}
          >
            <IoChevronBack />
          </a>
        </li>
        {pages}
        <li
          className={`page-item ${
            filter.page === totalPages ? "disabled" : ""
          }`}
        >
          <a
            className="page-link _avPointer"
            onClick={() => handlePageChange(filter.page + 1)}
          >
            <IoChevronForward />
          </a>
        </li>
      </ul>
    );
  };

  const fetchDeletedUserList = async (data) => {
    setLoading(true);
    try {
      const payload = {
        ...filter,
        userId: getUserData._id,
        keyword: filter?.keyword || "",
      };
      const res = await getDeletedUserList(payload);
      if (res) {
        setUserListData(res?.data?.users);
        setTotalCount(res?.data?.totalRecords);
        setLoading(false);
      } else {
        toast.error(res?.responseMessage);
        setLoading(false);
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
      setLoading(false);
    }
  };

  function formatDateToDDMMYYYY(isoString) {
    if (!isoString) return "";

    const date = new Date(isoString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  }

  const getUserRoleLabel = (role) => {
    switch (role) {
      case 1:
        return "User";
      case 2:
        return "Admin";
      case 3:
        return "Moderator";
      case 4:
        return "Business";
      default:
        return "-";
    }
  };

  const columns = [
    { header: "", accessor: "serial" },
    { header: "First Name", accessor: "firstName" },
    { header: "Last Name", accessor: "lastName" },
    { header: "Bird Name", accessor: "bird_name" },
    { header: "Sifarish Score", accessor: "sifarishPoints" },
    { header: "Phone no.", accessor: "phone_number" },
    { header: "Email", accessor: "emailId" },
    { header: "Location", accessor: "location" },
    { header: "Joined Date", accessor: "joined_date" },
    { header: "Deleted Date", accessor: "deleted_date" },
    { header: "Forwarded Count", accessor: "forwardedCount" },
    { header: "Created Count", accessor: "createdCount" },
    { header: "Helpful Count", accessor: "helpfulCount" },
    { header: "Generation", accessor: "generation" },
    { header: "Status", accessor: "activeStatus" },
    { header: "Role", accessor: "userRole" },
  ];

  const rowListData = (data) =>
    data?.map((item, i) => ({
      ...item,
      firstName: item?.firstName || "-",
      lastName: item?.lastName || "-",
      bird_name: item?.birdId?.title || "-",
      location: (
        <>
          {item?.city && item?.state && item?.country
            ? `${item?.city}, ${item?.state}, ${item?.country}`
            : "-"}
        </>
      ),
      phone_number: (
        <span>
          {`${item?.countryCode} ${item?.phoneNumber}` || "-"}
          {item?.isPhoneVerified ? (
            <FaCheckCircle style={{ marginLeft: "4px", color: "#08b231" }} />
          ) : (
            <MdCancel style={{ marginLeft: "4px", color: "#d62727" }} />
          )}
        </span>
      ),

      emailId: (
        <span>
          {item?.email || "-"}
          {item?.isEmailVerified ? (
            <FaCheckCircle style={{ marginLeft: "4px", color: "#08b231" }} />
          ) : (
            <MdCancel style={{ marginLeft: "4px", color: "#d62727" }} />
          )}
        </span>
      ),
      sifarishPoints: <>{item?.sifarishPoints || "-"}</>,
      forwardedCount: <>{item?.forwardedCount || "-"}</>,
      createdCount: <>{item?.createdCount || "-"}</>,
      helpfulCount: <>{item?.helpfulCount || "-"}</>,
      generation: <>{item?.generationId?.title || "-"}</>,
      activeStatus: item?.isActive ? "Active" : "Inactive",
      userRole: getUserRoleLabel(item?.userRole),
      joined_date: formatDateToDDMMYYYY(item?.createdAt),
      deleted_date: formatDateToDDMMYYYY(item?.deletedAt),
    }));

  const handleApply = (newRange) => {
    setFilter((s) => ({
      ...s,
      page: 1,
    }));
  };

  // const exportToCSV = async () => {
  //   try {
  //     const payload = {
  //       ...filter,
  //       userId: getUserData?._id,
  //       limit: -1,
  //     };

  //     const res = await getDeletedUserList(payload);

  //     if (!res || !res.data?.users || res.data.users.length === 0) {
  //       toast.error("No data to export!");
  //       return;
  //     }

  //     const fullData = res.data.users;

  //     const csvHeaders = [
  //       "Serial No, First Name, Last Name, Bird Name, Phone No., Email,Location, Status, Role, Joined Date,Deleted Date,Number Verified,Email Verified, Forwarded Count, Created Count, Helpful Count, Sifarish Score, Generation, ",
  //     ];

  //     const csvRows = fullData.map((user, index) => [
  //       index + 1,
  //       user?.firstName || "-",
  //       user?.lastName || "-",
  //       user?.birdId?.title || "-",
  //       user?.phoneNumber ? `${user?.countryCode} ${user?.phoneNumber}` : "-",
  //       user?.email || "-",
  //       `"${user?.city || "-"}, ${user?.state || "-"}, ${
  //         user?.country || "-"
  //       }"`,
  //       user?.isActive ? "Active" : "Inactive",
  //       getUserRoleLabel(user.userRole),
  //       formatDateToDDMMYYYY(user?.createdAt) || "-",
  //       formatDateToDDMMYYYY(user?.deletedAt) || "-",
  //       user?.isPhoneVerified ? "Yes" : "No",
  //       user?.isEmailVerified ? "Yes" : "No",
  //       user?.forwardedCount || "-",
  //       user?.createdCount || "-",
  //       user?.helpfulCount || "-",
  //       user?.sifarishPoints || "-",
  //       user?.generationId?.title || "-",
  //     ]);

  //     const csvContent =
  //       "data:text/csv;charset=utf-8," +
  //       [csvHeaders, ...csvRows.map((row) => row.join(","))].join("\n");

  //     const encodedUri = encodeURI(csvContent);
  //     const link = document.createElement("a");
  //     link.href = encodedUri;
  //     const epochTime = Date.now();
  //     link.download = `Deleted_Users_List_${epochTime}.csv`;
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //   } catch (error) {
  //     toast.error(error?.message || "Something went wrong during export.");
  //   }
  // };

  const exportToCSV = async () => {
    const loadingToast = toast.loading(
      "Please wait, Deleted Users data is getting exported..."
    );

    setExportLoad(true);
    try {
      const payload = {
        ...filter,
        userId: getUserData?._id,
        page: 1,
        limit: 1000,
      };

      const firstRes = await getDeletedUserList(payload);

      if (
        !firstRes ||
        !firstRes.data?.users ||
        firstRes.data.users.length === 0
      ) {
        toast.dismiss(loadingToast);
        toast.error("No data to export!");
        setExportLoad(false);
        return;
      }

      const totalPages = firstRes.data.totalPages || 1;
      const totalRecords =
        firstRes.data.totalRecords || firstRes.data.users.length;

      toast.dismiss(loadingToast);
      toast.success(
        `Exporting ${totalRecords} records in ${totalPages} chunks...`
      );

      let allData = [...firstRes.data.users];

      for (let page = 2; page <= totalPages; page++) {
        let success = false;
        let retries = 3;

        while (!success && retries > 0) {
          try {
            const res = await getDeletedUserList({ ...payload, page });
            if (res?.data?.users?.length) {
              allData = [...allData, ...res.data.users];
            }
            success = true;
          } catch (err) {
            console.warn(`Retrying page ${page}... (${3 - retries + 1})`);
            retries--;
            await new Promise((r) => setTimeout(r, 1000));
          }
        }
      }

      const chunkSize = 10000;
      const numFiles = Math.ceil(allData.length / chunkSize);

      for (let i = 0; i < numFiles; i++) {
        const chunk = allData.slice(i * chunkSize, (i + 1) * chunkSize);

        const filterSummary = [
          { Key: "Searched Word", Value: filter?.keyword || "-" },
        ];

        const formattedData = chunk.map((user, index) => ({
          "Serial No": i * chunkSize + index + 1,
          "First Name": user?.firstName || "-",
          "Last Name": user?.lastName || "-",
          "Bird Name": user?.birdId?.title || "-",
          "Phone No.": user?.phoneNumber
            ? `${user?.countryCode} ${user?.phoneNumber}`
            : "-",
          Email: user?.email || "-",
          Location: `${user?.city || "-"}, ${user?.state || "-"}, ${
            user?.country || "-"
          }`,
          Status: user?.isActive ? "Active" : "Inactive",
          Role: getUserRoleLabel(user.userRole),
          "Joined Date": formatDateToDDMMYYYY(user?.createdAt) || "-",
          "Deleted Date": formatDateToDDMMYYYY(user?.deletedAt) || "-",
          "Number Verified": user?.isPhoneVerified ? "Yes" : "No",
          "Email Verified": user?.isEmailVerified ? "Yes" : "No",
          "Forwarded Count": user?.forwardedCount || "-",
          "Created Count": user?.createdCount || "-",
          "Helpful Count": user?.helpfulCount || "-",
          "Sifarish Score": user?.sifarishPoints || "-",
          Generation: user?.generationId?.title || "-",
        }));

        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet([], { skipHeader: true });

        const keyColumn = "C";
        const valueColumn = "E";

        ws[`${keyColumn}1`] = { t: "s", v: "Filter Type" };
        ws[`${valueColumn}1`] = { t: "s", v: "Filter Value" };

        filterSummary.forEach((item, index) => {
          const rowNum = index + 3;
          ws[`${keyColumn}${rowNum}`] = { t: "s", v: item.Key };
          ws[`${valueColumn}${rowNum}`] = { t: "s", v: item.Value };
        });

        XLSX.utils.sheet_add_json(ws, formattedData, {
          origin: "A9",
          skipHeader: false,
        });

        const dataOnly = formattedData.map((r) => r);
        XLSX.utils.sheet_add_json(ws, dataOnly, {
          origin: "A10",
          skipHeader: true,
        });

        ws["!cols"] = [
          { wch: 8 },
          { wch: 20 },
          { wch: 20 },
          { wch: 25 },
          { wch: 18 },
          { wch: 25 },
          { wch: 40 },
          { wch: 12 },
          { wch: 15 },
          { wch: 20 },
          { wch: 20 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 },
          { wch: 15 },
        ];

        XLSX.utils.book_append_sheet(wb, ws, `Deleted_Users_${i + 1}`);

        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([wbout], { type: "application/octet-stream" });
        const epochTime = Date.now();
        saveAs(blob, `Deleted_Users_List_Part_${i + 1}_${epochTime}.xlsx`);
      }

      toast.success("All Deleted Users data exported successfully!");
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error?.message || "Something went wrong during export.");
    } finally {
      setExportLoad(false);
    }
  };

  const handleReset = () => {
    setFilter(initialFilter);
    setSearchParams({});
  };

  const hasActiveFilters = Object.keys(initialFilter).some((key) => {
    if (key === "page" || key === "limit") return false;

    const currentValue = filter[key];
    const initialValue = initialFilter[key];

    const normalize = (val) =>
      val === "" || val === null || val === undefined ? null : val;

    const curr = normalize(currentValue);
    const init = normalize(initialValue);

    return curr !== init;
  });

  return (
    <>
      <ButtonHeading
        title={`Deleted Users `}
        Export={true}
        ExportSubmit={exportToCSV}
        filter={filter}
        setFilter={setFilter}
        handleApply={handleApply}
        exportLoad={exportLoad}
        isShow={{
          keyword: true,
        }}
        handleReset={() => handleReset()}
        showResetFilter={hasActiveFilters}
      />

      <div className="_tabWith_outTab">
        <BootstrapTable
          columns={columns}
          data={rowListData(userListData?.length > 0 ? userListData : [])}
          isLoading={loading}
          colSpan={16}
        />
        <div className="paginationFt_avt">{renderPagination()}</div>
      </div>

      <div
        className={`offcanvas offcanvas-end common_offCanvas ${
          showFilterModal ? "show" : ""
        }`}
        tabIndex={-1}
        style={{ visibility: showFilterModal ? "visible" : "hidden" }}
      >
        {showFilterModal && (
          <MastersFilterModal
            isOpen={showFilterModal}
            onClose={() => setShowFilterModal(false)}
            filter={filter}
            setFilter={setFilter}
          />
        )}
      </div>
      <div className={showFilterModal ? "offBack_drop" : ""}></div>
    </>
  );
};

export default DeletedUser;
